import { useMemo } from 'react';
import type { ResponseTheme } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { transformPrimary, transformShade, transformColors } from './AppThemingContainer';

type Props = {
  children: React.ReactNode;
  /**
   * The theming data to provide to child components.
   * Pass the theme configuration directly instead of fetching.
   */
  theme: ResponseTheme;
  /**
   * Whether theming/whitelabeling is enabled.
   */
  enabled: boolean;
};

/**
 * A simpler alternative to AppThemingContainer for cases where
 * theming data is already available synchronously (no fetching needed).
 *
 * Use this when:
 * - You already have theming data from another source (e.g., Redux, another context)
 * - You don't need React Query's caching/refetching behavior
 *
 * Use AppThemingContainer when:
 * - You need to fetch theming data asynchronously
 * - You want React Query's caching and refetch capabilities
 *
 * @example
 * ```tsx
 * const theme = {
 *   app: { prompt: { documents: '' }, title: '' },
 *   color: { bubble: '#abc', primary: '#def', prompt: '#123' },
 *   enabled: true,
 *   logo: { horizontal: '', square: '' },
 * };
 *
 * <AppThemingProvider theme={theme} enabled={true}>
 *   <MyComponent />
 * </AppThemingProvider>
 * ```
 */
export const AppThemingProvider = (props: Props) => {
  const { theme, enabled, children } = props;

  const themingCtx = useMemo(() => {
    const mockQuery = {
      isFetching: false,
      isLoading: false,
      isPlaceholderData: false,
      isFetchedAfterMount: true,
      data: theme,
    };

    // Build palette using same transforms as AppThemingContainer
    const palette = {
      ...transformColors(theme?.color),
      primary: transformPrimary(theme?.color?.primary),
      shade: transformShade(theme?.color?.bubble),
      enabled,
    };

    return {
      enabled,
      query: mockQuery,
      palette,
      theme,
    };
  }, [theme, enabled]);

  return <AppThemingContext.Provider value={themingCtx}>{children}</AppThemingContext.Provider>;
};

AppThemingProvider.displayName = 'AppTheming.Provider';
